import _ from "lodash";
import { ITileSettings } from "components/product-collection/manage-tile-view/types";
import {
    IViewDefinition,
    IViewDefinitionElement,
    SECTIONS,
    ViewDefinitionFormatter,
    ViewDefinitionFormatterType,
} from "types/view-definitions";
import moment from "moment";

export type ViewDefinitionConfig = Pick<
    ITileSettings,
    | "firstLine"
    | "secondLine"
    | "thirdLine"
    | "fourthLine"
    | "fifthLine"
    | "sixthLine"
    | "seventhLine"
    | "eighthLine"
    | "showColorSwatch"
    | "viewDef"
    | "totalCostLabel"
    | "totalRetailLabel"
>;

export enum ProductRequiredDisplayFields {
    NAME = "name",
    COLOR = "color",
    STYLE_NUMBER = "style_number",
    SEASON = "season",
}

/**
 * Generates tile view settings for first, third and second line using the order of section details in the given line item view definition.
 * TODO GV: add VD formatters
 */
export const generateViewDefinitionConfig = (itemView: IViewDefinition): ViewDefinitionConfig => {
    const fields = getViewDefFields(itemView);
    const lines = _.without(fields, ProductRequiredDisplayFields.NAME, ProductRequiredDisplayFields.COLOR);
    const isDisplayed = (field) => _.includes(fields, field);

    // Second will always be product name. 1, 3 and 4 will follow the order set from view definitions from settings.
    // This is because 2nd line has strong appearance from component side.
    return {
        firstLine: lines[0] ?? "",
        secondLine: isDisplayed(ProductRequiredDisplayFields.NAME) ? ProductRequiredDisplayFields.NAME : "",
        thirdLine: lines[1] ?? "",
        fourthLine: isDisplayed(ProductRequiredDisplayFields.COLOR)
            ? ProductRequiredDisplayFields.COLOR
            : (lines[2] ?? ""),
        fifthLine: lines[3] ?? "",
        sixthLine: lines[4] ?? "",
        seventhLine: lines[5] ?? "",
        eighthLine: lines[6] ?? "",
        showColorSwatch: isDisplayed(ProductRequiredDisplayFields.COLOR),
        viewDef: {
            showImage: isSmallImageShown(itemView),
            fields,
        },
    };
};

export const getViewDefFields = (view: IViewDefinition): string[] =>
    _.map(view?.sections?.[SECTIONS.DETAILS]?.elements, "reference");

export const isSmallImageShown = (view: IViewDefinition) => !!view?.sections?.[SECTIONS.SMALL_IMAGE];

export const applyFormatters = (value: string | string[], field: IViewDefinitionElement) =>
    _.reduce(
        field.formatters,
        (value, formatter) => FORMATTERS[formatter.formatter]?.(value, formatter) ?? value,
        value,
    );

export const FORMATTERS = {
    [ViewDefinitionFormatterType.Uppercase]: (x) => _.toUpper(_.toString(x)),
    [ViewDefinitionFormatterType.Lowercase]: (x) => _.toLower(_.toString(x)),
    [ViewDefinitionFormatterType.SentenceCase]: (x) => _.capitalize(_.toString(x)),
    [ViewDefinitionFormatterType.CapitalizeWords]: (x) => _.startCase(_.toString(x)),
    [ViewDefinitionFormatterType.Width]: (x) => x, // not applicable in cart
    [ViewDefinitionFormatterType.Truncate]: (x, formatter: ViewDefinitionFormatter) =>
        _.truncate(_.toString(x), { length: _.toNumber(formatter.params[0]) }),
    [ViewDefinitionFormatterType.Date]: (x, { params }: ViewDefinitionFormatter) => {
        const format = params[0]
            .replace("%mon", "MMM")
            .replace("%mm", "M")
            .replace("%0mm", "MM")
            .replace("%dd", "D")
            .replace("%0dd", "DD")
            .replace("%yyyy", "YYYY");
        return moment.utc(x).format(format);
    },
};
